import type { AudioSettings } from '@cinegrid/core';
import { defaultAudioSettings } from '@cinegrid/core';

/** Normalize older projects that lack clip.properties.audio */
export function resolveAudioSettings(
  audio: AudioSettings | undefined | null
): AudioSettings {
  const d = defaultAudioSettings();
  if (!audio) return d;
  return {
    volume: audio.volume ?? d.volume,
    noiseRemoval: { ...d.noiseRemoval, ...audio.noiseRemoval },
    reverb: { ...d.reverb, ...audio.reverb },
    eq: { ...d.eq, ...audio.eq },
    compressor: { ...d.compressor, ...audio.compressor },
  };
}

export function createReverbImpulse(
  ctx: BaseAudioContext,
  seconds: number,
  decay: number
): AudioBuffer {
  const rate = ctx.sampleRate;
  const length = Math.max(1, Math.floor(rate * Math.max(0.05, seconds)));
  const impulse = ctx.createBuffer(2, length, rate);
  for (let ch = 0; ch < impulse.numberOfChannels; ch++) {
    const data = impulse.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 1 + decay * 4);
    }
  }
  return impulse;
}

export type AudioFxHandles = {
  input: AudioNode;
  output: AudioNode;
  volume: GainNode;
  hp: BiquadFilterNode;
  eqLow: BiquadFilterNode;
  eqMid: BiquadFilterNode;
  eqHigh: BiquadFilterNode;
  compressor: DynamicsCompressorNode;
  makeup: GainNode;
  dry: GainNode;
  wet: GainNode;
  convolver: ConvolverNode;
  apply: (settings: AudioSettings) => void;
  disconnect: () => void;
};

/**
 * Build: input → volume → HP → EQ → compressor → makeup → dry/wet reverb → output
 */
export function createAudioFxChain(ctx: BaseAudioContext): AudioFxHandles {
  const volume = ctx.createGain();
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 20;
  hp.Q.value = 0.7;

  const eqLow = ctx.createBiquadFilter();
  eqLow.type = 'lowshelf';
  eqLow.frequency.value = 250;

  const eqMid = ctx.createBiquadFilter();
  eqMid.type = 'peaking';
  eqMid.frequency.value = 1000;
  eqMid.Q.value = 1;

  const eqHigh = ctx.createBiquadFilter();
  eqHigh.type = 'highshelf';
  eqHigh.frequency.value = 4000;

  const compressor = ctx.createDynamicsCompressor();
  const makeup = ctx.createGain();

  const dry = ctx.createGain();
  const wet = ctx.createGain();
  const convolver = ctx.createConvolver();
  convolver.buffer = createReverbImpulse(ctx, 1.8, 0.45);

  const merger = ctx.createGain(); // mix dry+wet

  volume.connect(hp);
  hp.connect(eqLow);
  eqLow.connect(eqMid);
  eqMid.connect(eqHigh);
  eqHigh.connect(compressor);
  compressor.connect(makeup);
  makeup.connect(dry);
  makeup.connect(convolver);
  convolver.connect(wet);
  dry.connect(merger);
  wet.connect(merger);

  let lastReverbSize = -1;

  const apply = (raw: AudioSettings) => {
    const s = resolveAudioSettings(raw);
    volume.gain.value = Math.max(0, Math.min(2, s.volume));

    if (s.noiseRemoval.enabled && s.noiseRemoval.amount > 0.001) {
      hp.frequency.value = 40 + s.noiseRemoval.amount * 410;
    } else {
      hp.frequency.value = 20;
    }

    if (s.eq.enabled) {
      eqLow.gain.value = s.eq.low;
      eqMid.gain.value = s.eq.mid;
      eqHigh.gain.value = s.eq.high;
    } else {
      eqLow.gain.value = 0;
      eqMid.gain.value = 0;
      eqHigh.gain.value = 0;
    }

    if (s.compressor.enabled) {
      compressor.threshold.value = s.compressor.threshold;
      compressor.knee.value = 12;
      compressor.ratio.value = s.compressor.ratio;
      compressor.attack.value = Math.max(0.001, s.compressor.attack);
      compressor.release.value = Math.max(0.01, s.compressor.release);
      makeup.gain.value = 1 + s.compressor.makeup * 2;
    } else {
      compressor.threshold.value = 0;
      compressor.ratio.value = 1;
      compressor.knee.value = 0;
      compressor.attack.value = 0.003;
      compressor.release.value = 0.25;
      makeup.gain.value = 1;
    }

    const wetAmt =
      s.reverb.enabled && s.reverb.amount > 0.001 ? s.reverb.amount : 0;
    dry.gain.value = 1 - wetAmt * 0.85;
    wet.gain.value = wetAmt;
    if (Math.abs(s.reverb.size - lastReverbSize) > 0.02) {
      lastReverbSize = s.reverb.size;
      const seconds = 0.4 + s.reverb.size * 2.6;
      convolver.buffer = createReverbImpulse(ctx, seconds, s.reverb.size);
    }
  };

  apply(defaultAudioSettings());

  return {
    input: volume,
    output: merger,
    volume,
    hp,
    eqLow,
    eqMid,
    eqHigh,
    compressor,
    makeup,
    dry,
    wet,
    convolver,
    apply,
    disconnect: () => {
      try {
        volume.disconnect();
        hp.disconnect();
        eqLow.disconnect();
        eqMid.disconnect();
        eqHigh.disconnect();
        compressor.disconnect();
        makeup.disconnect();
        dry.disconnect();
        wet.disconnect();
        convolver.disconnect();
        merger.disconnect();
      } catch {
        /* already disconnected */
      }
    },
  };
}
